import type { GetStaticProps } from 'next'
import Link from 'next/link'
import { CtaBar } from '@/components/Seo/ContentBlocks'
import { GUIDES, SPORTS, STATS } from '@/lib/seoContent'
import { absUrl, breadcrumbLd, SeoProps } from '@/utils/seo'

type Card = { slug: string; h1: string; tldr: string }

const GuideIndex = ({ guides, sports }: { guides: Card[]; sports: { slug: string; name: string; h1: string }[] }) => (
  <div className="min-h-screen bg-gray-50">
    <div className="mx-auto flex max-w-5xl flex-col gap-10 px-4 py-10 md:py-14">
      <header className="flex flex-col items-center gap-3 text-center">
        <h1 className="text-4xl font-black tracking-tight text-gray-900">办赛指南</h1>
        <p className="max-w-2xl text-gray-500">
          瑞士制、团团转、复式赛、循环赛、小组淘汰赛……把赛制讲清楚,把比赛办顺利。灵缇赛事系统自 {STATS.since} 上线,已承办 {STATS.matches} 场比赛。
        </p>
      </header>
      <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {guides.map((g) => (
          <Link key={g.slug} href={`/guide/${g.slug}`} className="flex flex-col gap-2 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-black/5 transition hover:shadow-md">
            <h2 className="text-lg font-bold text-gray-900">{g.h1}</h2>
            <p className="line-clamp-3 text-sm leading-relaxed text-gray-500">{g.tldr}</p>
          </Link>
        ))}
      </section>
      <section className="flex flex-col gap-3">
        <h2 className="text-xl font-bold text-gray-900">按项目办赛</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          {sports.map((s) => (
            <Link key={s.slug} href={`/sports/${s.slug}`} className="rounded-xl bg-white p-4 text-center font-semibold text-gray-800 ring-1 ring-black/5 hover:text-[#DB3142]">
              {s.name}
            </Link>
          ))}
        </div>
      </section>
      <CtaBar />
    </div>
  </div>
)

export const getStaticProps: GetStaticProps = async () => {
  const guides = GUIDES.map(({ slug, h1, tldr }) => ({ slug, h1, tldr }))
  const sports = SPORTS.map(({ slug, name, h1 }) => ({ slug, name, h1 }))
  const seo: SeoProps = {
    title: '办赛指南 · 瑞士制/团团转/复式赛/循环赛/小组淘汰赛怎么编排 | 灵缇赛事系统',
    description: '灵缇办赛指南:瑞士制编排规则、团团转记分、复式赛与米切尔/豪厄尔移位、掼蛋计分排名、循环赛场次、小组循环淘汰赛、联赛,以及如何组织一场掼蛋比赛的完整流程。',
    keywords: '办赛指南,比赛编排,瑞士制,团团转,复式赛,循环赛,小组淘汰赛,掼蛋比赛怎么组织,赛事编排方法',
    path: '/guide',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '灵缇办赛指南',
        url: absUrl('/guide'),
        mainEntity: {
          '@type': 'ItemList',
          itemListElement: guides.map((g, i) => ({ '@type': 'ListItem', position: i + 1, url: absUrl(`/guide/${g.slug}`), name: g.h1 })),
        },
      },
      breadcrumbLd([
        ['首页', '/'],
        ['办赛指南', '/guide'],
      ]),
    ],
  }
  return { props: { guides, sports, seo } }
}

export default GuideIndex
